import {
  ErrorHandler,
  Inject,
  Injectable,
  InjectionToken,
} from '@angular/core';
import { environment } from '../../../environments/environment';
import type Rollbar from 'rollbar';

// IMPORTANT: Do not import Rollbar directly, it breaks on ad block, use:
//  @Inject(RollbarService) private rollbar: RollbarService
export const RollbarService = new InjectionToken<RollbarService>('rollbar');
export type RollbarService = Rollbar;

@Injectable()
export class RollbarErrorHandler implements ErrorHandler {
  constructor(@Inject(RollbarService) private rollbar: RollbarService) {}

  handleError(err: any): void {
    console.error('Uncaught error (sent to Rollbar)', err);
    this.rollbar.error(err?.originalError ?? err);
  }
}

// The Rollbar module connects to Rollbar servers on import, and this can
// result in an ERR_BLOCKED_BY_CLIENT, and results in a blank screen. So instead
// of importing it directly we start a global promise to import rollbar, and
// silence any errors.
const rollbarImportPromise = import('rollbar').catch(() =>
  console.warn('Rollbar import blocked.'),
);

// We then use a wrapper that proxies the Rollbar class.
class RollbarWrapper {
  constructor() {
    // Each wrapper tracks a Rollbar instance
    let rollbarInstance: any;
    // Start a promise to construct a Rollbar object from the global import promise.
    const localPromise = rollbarImportPromise.then((rollbarModule) => {
      if (rollbarModule)
        rollbarInstance = new rollbarModule.default({ ...environment.rollbar });
    });
    // Return proxy from the constructor so that we can intercept property access
    return new Proxy(this, {
      get: (_, property) => {
        // If the rollbar instance has been loaded, simply proxy its property
        if (rollbarInstance) return rollbarInstance[property];
        // If not, pray to God that someone is trying to call one of its error
        // functions and use an async function that awaits the rollbar import
        else
          return async (...args: any[]) => {
            await localPromise;
            if (!rollbarInstance) return undefined;
            // Defer the original function call to the rollbar instance and
            // pray everything runs smoothly from now on.
            return rollbarInstance[property](...args);
          };
      },
    });
  }
}

export function rollbarFactory() {
  // Create an adblock import-failure proof wrapper around Rollbar
  return new RollbarWrapper() as RollbarService;
}
